/**
 * E-GTCC-13 + E-GTCC-14 — Tenants list and Tenant 360.
 * I-GTCC-18 ports tenant creation; I-GTCC-19 ports tenant lifecycle actions;
 * I-GTCC-22 reuses the complete tenant-device management card.
 *
 * Reads exactly the endpoints gtcc1 already reads, nothing new:
 *   - `GET /v1/admin/tenants`                        (`apps/admin/src/tenants/TenantsList.tsx:111`)
 *   - `GET /v1/admin/tenants/:id`                    (`apps/admin/src/tenants/TenantDetailPage.tsx:322`)
 *   - `GET /v1/admin/tenants/:id/members`            (`apps/admin/src/tenants/TenantMembersCard.tsx`)
 *   - `GET /v1/admin/tenants/:id/devices`            (`apps/admin/src/tenants/TenantDevicesCard.tsx`)
 *   - `GET /v1/admin/tenants/:id/launch-status`      (`apps/admin/src/tenants/TenantLaunchStatusCard.tsx:47`)
 *   - `GET /v1/admin/tenants/:id/ai-capabilities`    (`apps/admin/src/tenants/TenantDetailPage.tsx:331`)
 *   - `GET /v1/admin/tenants/:id/ai-usage`           (`apps/admin/src/tenants/TenantDetailPage.tsx:345`)
 * Writes only through existing Admin Console routes:
 *   - `POST /v1/admin/tenants/:id/lifecycle/{pause|resume|suspend|cancel}`
 *   - `POST /v1/admin/tenants/:id/devices`
 *   - `DELETE /v1/admin/tenants/:id/devices/:deviceId` (soft or confirmed hard delete)
 *   - `POST /v1/admin/tenants/:id/devices/:deviceId/{move|restore|suspend}`
 *   - `PATCH /v1/admin/tenants/:id/members/:memberId/role`
 *   - `PATCH /v1/admin/tenants/:id/members/:memberId/status`
 *   - `DELETE /v1/admin/tenants/:id/members/:memberId?confirm=hard`
 *
 * Domain readiness has no endpoint at all in gtcc1 — the card derives the
 * future tenant URL client-side from the stored slug
 * (`apps/admin/src/tenants/TenantDomainReadinessCard.tsx`). This view keeps
 * the derivation and drops the editable input with it.
 *
 * Every other write affordance of gtcc1 (edit drawer, entitlements,
 * onboard-admin) is absent — not disabled, absent.
 *
 * B-GTCC-A10 (register row 20) — the tenant's entitlements are rendered
 * read-only from the tenant GET this view already issues. The route serialises
 * both halves side by side: the raw `entitlements` JSONB the operator wrote and
 * the server-computed `resolvedEntitlements`
 * (`services/api/src/modules/admin/tenants.routes.ts:112`). The section shows
 * the effective value and marks every key the operator never set as a
 * type-fallback, so an empty JSONB no longer reads as "nothing configured".
 * gtcc1's entitlements EDITOR (`apps/admin/src/tenants/TenantEntitlementsDialog.tsx`)
 * stays out: no dialog, no PATCH, no new endpoint. Row 20 therefore moves to JA
 * for the reading half only.
 *
 * B-GTCC-A11 — the list carries a lifecycle filter next to the search. It is
 * purely client-side over the rows the list GET already returned: no query
 * parameter, no second request, no endpoint. Its options are derived from the
 * data, so an unused lifecycle is not offerable, and the hit counter names how
 * much of the answer is currently visible.
 *
 * The page switch is view-local state, the same shape gtcc1 uses for its
 * own page switch (`apps/admin/src/AdminApp.tsx:299`); no router is added.
 *
 * Each section owns its five states: loading, forbidden (HTTP 403 on that
 * one section, never a page-level failure), error, empty, data.
 *
 * I-GTCC-22 (register row 22, "Tenant-Geraete verwalten") — Tenant 360 reuses
 * the complete `TenantDevicesCard` from gtcc1 instead of maintaining a second
 * read-only implementation. That single component owns list, create, move,
 * archive, suspend, restore and identifier-confirmed hard delete. No endpoint,
 * service or schema is duplicated here.
 * B-GTCC-A9 (register rows 21 and 23) — the tenant's invitations are rendered
 * read-only from the list endpoint gtcc1 already reads
 * (`GET /v1/admin/tenants/:id/invitations`,
 * `apps/admin/src/invitations/useInvitations.ts:64`). Read-only means the
 * whole write half of gtcc1's invitations screen is absent, not disabled: no
 * invite, no resend, no revoke, no hard delete, no new endpoint. Row 23
 * therefore moves to TEILWEISE (nur lesend).
 *
 * C-GTCC-21-WRITE1 (register row 21, Tenant-Mitglieder verwalten) — the
 * members section gains exactly the three write actions gtcc1's own card
 * already owns (`apps/admin/src/tenants/TenantMembersCard.tsx`): role change,
 * suspend/reactivate and the identifier-confirmed hard removal. They call the
 * existing platform-owner routes
 * (`services/api/src/modules/admin/platform-members.routes.ts:99`, `:133`,
 * `:167`) — no new endpoint, no new right, the same
 * `requirePlatformOwner("tenants")` guard. The client mirrors the server's
 * last-active-owner guard by disabling the shielded controls; the 409 from the
 * route stays the authority. Row 21 therefore moves to JA.
 */
import {
  Button,
  ConfirmDialog,
  DataTable,
  DeleteWithConfirm,
  EmptyState,
  ErrorState,
  LoadingState,
  StatePill,
  TextInput,
  type DataTableColumn,
  type StatePillTone,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { ApiError, UnauthenticatedError, apiGet, apiSend } from "../../../lib/api.js";
import { formatCount } from "../../../monitoring/utils.js";
import {
  allowedTenantLifecycleVerbs,
  tenantLifecyclePath,
  type TenantLifecycleVerb,
} from "../../../tenants/tenantLifecycle.js";
import { DEFAULT_TENANT_BASE_DOMAIN, buildTenantUrl } from "../../../tenants/tenantSlug.js";
import { TenantDevicesCard } from "../../../tenants/TenantDevicesCard.js";
import { resolveMinWidthBreakpointQuery, useBreakpointMatch } from "../../breakpoint.js";
import { GtccCreateTenantForm } from "./GtccCreateTenantForm.js";
import { GtccTenantFeaturesCard } from "./GtccTenantFeaturesCard.js";

/* ------------------------------------------------------------------ types */

/**
 * Lifecycle in gtcc1 is the tenant's `subscriptionStatus`; the closed set and
 * the tone mapping are copied from `apps/admin/src/tenants/TenantsList.tsx`.
 */
const LIFECYCLE_STATUSES = [
  "trial",
  "active",
  "paused",
  "suspended",
  "cancelled",
  "expired",
] as const;
type LifecycleStatus = (typeof LIFECYCLE_STATUSES)[number];

/** Sentinel of the lifecycle filter: not a status, so it cannot collide with one. */
const LIFECYCLE_ALL = "all";

const LIFECYCLE_TONES: Record<LifecycleStatus, StatePillTone> = {
  trial: "info",
  active: "success",
  paused: "warn",
  suspended: "warn",
  cancelled: "danger",
  expired: "neutral",
};

/** Tenant type as gtcc1 models it (apps/admin/src/tenants/TenantsList.tsx:34). */
type TenantType = "platform_only" | "managed_hardware" | "hybrid";

interface TenantRowDto {
  readonly id: string;
  readonly displayName: string | null;
  readonly type?: TenantType;
  readonly subscriptionStatus?: LifecycleStatus;
  readonly maxDevices?: number | null;
  readonly createdAt?: string;
}

interface TenantsListResponse {
  readonly items?: readonly TenantRowDto[];
}

/**
 * The closed entitlements JSONB shape
 * (`services/api/src/db/schema/tenants.ts:196`). The tenant GET serialises the
 * raw operator value under `entitlements` and the resolved policy under
 * `resolvedEntitlements`, both in this same shape.
 */
interface EntitlementsDto {
  readonly devices?: {
    readonly self_register?: string | null;
    readonly approval_required?: boolean | null;
    readonly imei_edit?: string | null;
    readonly delete?: string | null;
  } | null;
  readonly managed_hardware_only?: boolean | null;
  readonly allowed_vendors?: readonly string[] | null;
}

interface TenantDetail {
  readonly id: string;
  readonly displayName?: string;
  readonly slug?: string | null;
  readonly type?: string;
  readonly subscriptionStatus?: LifecycleStatus;
  readonly trialUntil?: string | null;
  readonly validUntil?: string | null;
  readonly maxDevices?: number | null;
  readonly maxUsers?: number | null;
  readonly entitlements?: EntitlementsDto | null;
  readonly resolvedEntitlements?: EntitlementsDto | null;
}

interface TenantDetailResponse {
  readonly tenant?: TenantDetail;
}

interface TenantLifecycleResponse {
  readonly tenant: TenantDetail;
}

interface MemberRow {
  readonly id: string;
  readonly email?: string | null;
  readonly displayName?: string | null;
  readonly role?: string;
  readonly status?: string;
}

interface MembersResponse {
  readonly items?: readonly MemberRow[];
}

/**
 * The closed role set the control-plane route accepts
 * (`services/api/src/modules/admin/platform-members.routes.ts:99`).
 */
const MEMBER_ROLES = ["owner", "admin", "dispatcher", "viewer"] as const;
type MemberRole = (typeof MEMBER_ROLES)[number];

/** The confirmable member writes; the hard removal has its own typed gate. */
type MemberWriteAction =
  | { readonly kind: "role"; readonly member: MemberRow; readonly role: MemberRole }
  | { readonly kind: "suspend"; readonly member: MemberRow }
  | { readonly kind: "reactivate"; readonly member: MemberRow };

/**
 * The invitation fields the API actually serialises — measured in
 * `services/api/src/modules/admin/platform-invitations.service.ts:65`
 * (`toDto`). `status` is the stored status except that a pending invitation
 * whose `tokenExpiresAt` has passed is reported as `expired`.
 */
interface InvitationRow {
  readonly id: string;
  readonly email?: string | null;
  readonly displayName?: string | null;
  readonly role?: string | null;
  readonly status?: string | null;
  readonly invitedByEmail?: string | null;
  readonly tokenExpiresAt?: string | null;
  readonly createdAt?: string | null;
}

interface InvitationsResponse {
  readonly items?: readonly InvitationRow[];
}

/**
 * The closed status set and its tones, copied from
 * `apps/admin/src/invitations/types.ts:8` plus the computed `expired`.
 */
const INVITATION_TONES: Record<string, StatePillTone> = {
  pending: "warn",
  accepted: "success",
  revoked: "danger",
  expired: "neutral",
};

/**
 * The launch checklist items gtcc1 renders (TenantLaunchStatusCard.tsx:58).
 */
type LaunchItemId =
  | "tenant_exists"
  | "subscription_active"
  | "first_admin_invited"
  | "first_admin_active"
  | "vehicle_count"
  | "device_count"
  | "latest_email_delivery";
type LaunchItemStatus = "ready" | "needs_action" | "blocked" | "manual";

const LAUNCH_TONES: Record<LaunchItemStatus, StatePillTone> = {
  ready: "success",
  needs_action: "warn",
  blocked: "danger",
  manual: "info",
};

interface LaunchStatusResponse {
  readonly overall?: LaunchItemStatus;
  readonly items?: readonly { readonly id: LaunchItemId; readonly status: LaunchItemStatus }[];
  readonly computedAt?: string;
}

interface AiCapabilitiesResponse {
  readonly aiCapabilities?: {
    readonly gfiAiQuestionsEnabled?: boolean;
    readonly aiReportsEnabled?: boolean;
    readonly mode?: "disabled" | "dry_run" | "enabled";
    readonly gfiProvider?: string | null;
    readonly gfiModel?: string | null;
    readonly reportsProvider?: string | null;
    readonly reportsModel?: string | null;
  };
}

interface AiUsageResponse {
  readonly usage?: {
    readonly costVerified?: boolean;
    readonly totals?: {
      readonly invocations?: number;
      readonly dryRunCount?: number;
      readonly providerCalledCount?: number;
      readonly okCount?: number;
      readonly estimatedCostMicros?: number;
    };
  };
}

/* ---------------------------------------------------------------- helpers */

function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function providerLabel(provider: string | null | undefined, model: string | null | undefined) {
  if (!provider) return "—";
  return model ? `${provider} / ${model}` : provider;
}

/* ------------------------------------------------------- entitlement rows */

/** One entitlement key, with its effective value and where that value came from. */
interface EntitlementRow {
  readonly id: string;
  readonly labelKey: string;
  readonly effective: string;
  readonly policy: string;
  /** True when the operator never wrote the key and the type default applies. */
  readonly fallback: boolean;
}

type Translate = (key: string, vars?: Record<string, string | number>) => string;

/**
 * Turns the two serialised halves into one row per entitlement key. `effective`
 * comes from `resolvedEntitlements` (and falls back to the raw value when the
 * route did not resolve one), `policy` prints the raw operator value or a dash;
 * `fallback` is true exactly when the raw value is absent.
 */
function buildEntitlementRows(
  detail: TenantDetail | undefined,
  t: Translate,
): readonly EntitlementRow[] {
  const raw = detail?.entitlements ?? null;
  const resolved = detail?.resolvedEntitlements ?? null;

  const policyWord = (value: string | null | undefined): string =>
    value ? t(`admin.gtcc.ten.entitlements.value.${value}`) : "—";
  const yesNo = (value: boolean | null | undefined): string =>
    value === null || value === undefined
      ? "—"
      : value
        ? t("admin.gtcc.ten.entitlements.value.yes")
        : t("admin.gtcc.ten.entitlements.value.no");
  const vendors = (value: readonly string[] | null | undefined): string => {
    if (value === null || value === undefined) return "—";
    return value.length === 0
      ? t("admin.gtcc.ten.entitlements.value.no_vendor_restriction")
      : [...value].join(", ");
  };
  const effectiveOf = (resolvedText: string, rawText: string): string =>
    resolvedText === "—" ? rawText : resolvedText;

  return [
    {
      id: "self_register",
      labelKey: "admin.gtcc.ten.entitlements.row.self_register",
      effective: effectiveOf(
        policyWord(resolved?.devices?.self_register),
        policyWord(raw?.devices?.self_register),
      ),
      policy: policyWord(raw?.devices?.self_register),
      fallback: raw?.devices?.self_register === null || raw?.devices?.self_register === undefined,
    },
    {
      id: "approval_required",
      labelKey: "admin.gtcc.ten.entitlements.row.approval_required",
      effective: effectiveOf(
        yesNo(resolved?.devices?.approval_required),
        yesNo(raw?.devices?.approval_required),
      ),
      policy: yesNo(raw?.devices?.approval_required),
      fallback:
        raw?.devices?.approval_required === null || raw?.devices?.approval_required === undefined,
    },
    {
      id: "imei_edit",
      labelKey: "admin.gtcc.ten.entitlements.row.imei_edit",
      effective: effectiveOf(
        policyWord(resolved?.devices?.imei_edit),
        policyWord(raw?.devices?.imei_edit),
      ),
      policy: policyWord(raw?.devices?.imei_edit),
      fallback: raw?.devices?.imei_edit === null || raw?.devices?.imei_edit === undefined,
    },
    {
      id: "delete",
      labelKey: "admin.gtcc.ten.entitlements.row.delete",
      effective: effectiveOf(
        policyWord(resolved?.devices?.delete),
        policyWord(raw?.devices?.delete),
      ),
      policy: policyWord(raw?.devices?.delete),
      fallback: raw?.devices?.delete === null || raw?.devices?.delete === undefined,
    },
    {
      id: "managed_hardware_only",
      labelKey: "admin.gtcc.ten.entitlements.row.managed_hardware_only",
      effective: effectiveOf(
        yesNo(resolved?.managed_hardware_only),
        yesNo(raw?.managed_hardware_only),
      ),
      policy: yesNo(raw?.managed_hardware_only),
      fallback: raw?.managed_hardware_only === null || raw?.managed_hardware_only === undefined,
    },
    {
      id: "allowed_vendors",
      labelKey: "admin.gtcc.ten.entitlements.row.allowed_vendors",
      effective: effectiveOf(vendors(resolved?.allowed_vendors), vendors(raw?.allowed_vendors)),
      policy: vendors(raw?.allowed_vendors),
      fallback: raw?.allowed_vendors === null || raw?.allowed_vendors === undefined,
    },
  ];
}

/* ------------------------------------------------------------- primitives */

/** Loading / forbidden / error / empty / content — identical in every section. */
function QuerySection<T>({
  query,
  loadingLabel,
  forbiddenTitle,
  errorTitle,
  emptyTitle,
  isEmpty,
  render,
  testId,
}: {
  readonly query: UseQueryResult<T, Error>;
  readonly loadingLabel: string;
  readonly forbiddenTitle: string;
  readonly errorTitle: string;
  readonly emptyTitle: string;
  readonly isEmpty: (data: T) => boolean;
  readonly render: (data: T) => ReactNode;
  readonly testId: string;
}) {
  if (query.isPending) {
    return (
      <div data-testid={`${testId}-loading`}>
        <LoadingState label={loadingLabel} />
      </div>
    );
  }
  if (query.isError) {
    if (isForbidden(query.error)) {
      return (
        <div data-testid={`${testId}-forbidden`}>
          <ErrorState title={forbiddenTitle} severity="warn" />
        </div>
      );
    }
    return (
      <div data-testid={`${testId}-error`}>
        <ErrorState title={errorTitle} body={query.error.message} />
      </div>
    );
  }
  if (isEmpty(query.data)) {
    return (
      <div data-testid={`${testId}-empty`}>
        <EmptyState title={emptyTitle} />
      </div>
    );
  }
  return <div data-testid={`${testId}-data`}>{render(query.data)}</div>;
}

function TileGrid({ testId, children }: { readonly testId: string; readonly children: ReactNode }) {
  const multiColumn = useBreakpointMatch(resolveMinWidthBreakpointQuery);
  return (
    <div
      className="gtcc-ten-tile-grid"
      data-testid={testId}
      style={{
        display: "grid",
        gap: "var(--gt-sys-space-3, 12px)",
        gridTemplateColumns: multiColumn ? "repeat(auto-fit, minmax(160px, 1fr))" : "1fr",
      }}
    >
      {children}
    </div>
  );
}

function Tile({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div
      className="gtcc-ten-tile"
      data-testid="gtcc-ten-tile"
      aria-label={`${label}: ${value}`}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gt-sys-space-1, 4px)",
        padding: "var(--gt-sys-space-3, 12px)",
        border: "1px solid var(--gt-sys-border, #e5e7eb)",
        borderRadius: "var(--gt-sys-radius-md, 8px)",
        background: "var(--gt-sys-surface-raised, #f9fafb)",
      }}
    >
      <span
        style={{
          fontSize: "var(--gt-sys-font-size-xs, 12px)",
          color: "var(--gt-sys-text-muted, #6b7280)",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: "var(--gt-sys-font-size-lg, 15px)",
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function SectionHeading({ children }: { readonly children: ReactNode }) {
  return (
    <h3 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-md, 14px)", fontWeight: 600 }}>
      {children}
    </h3>
  );
}

function Section({
  testId,
  heading,
  children,
}: {
  readonly testId: string;
  readonly heading: string;
  readonly children: ReactNode;
}) {
  return (
    <section
      data-testid={testId}
      style={{ display: "flex", flexDirection: "column", gap: "var(--gt-sys-space-2, 8px)" }}
    >
      <SectionHeading>{heading}</SectionHeading>
      {children}
    </section>
  );
}

/** Label / value pair used by the Tenant 360 header and the small read-only lists. */
function Field({ label, value }: { readonly label: string; readonly value: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--gt-sys-space-1, 4px)" }}>
      <span
        style={{
          fontSize: "var(--gt-sys-font-size-xs, 12px)",
          color: "var(--gt-sys-text-muted, #6b7280)",
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: "var(--gt-sys-font-size-md, 14px)" }}>{value}</span>
    </div>
  );
}

function FieldGrid({ children }: { readonly children: ReactNode }) {
  const multiColumn = useBreakpointMatch(resolveMinWidthBreakpointQuery);
  return (
    <div
      style={{
        display: "grid",
        gap: "var(--gt-sys-space-3, 12px)",
        gridTemplateColumns: multiColumn ? "repeat(auto-fit, minmax(180px, 1fr))" : "1fr",
      }}
    >
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------- list */

function TenantsListSection({
  selectedId,
  onSelect,
}: {
  readonly selectedId: string | null;
  readonly onSelect: (id: string) => void;
}) {
  const { t } = useLocale();
  const [search, setSearch] = useState("");
  const [lifecycle, setLifecycle] = useState<string>(LIFECYCLE_ALL);
  const query = useQuery<TenantsListResponse, Error>({
    queryKey: ["gtcc", "ten", "tenants"],
    queryFn: ({ signal }) => apiGet<TenantsListResponse>("/v1/admin/tenants", undefined, signal),
    staleTime: 30_000,
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);

  /*
    The options are derived from the answer, never from a hard-coded list: a
    lifecycle nobody is in must not be offerable, and a value the backend
    starts sending must appear without a frontend change. `LIFECYCLE_STATUSES`
    only fixes the ORDER, so the select does not reshuffle between responses.
  */
  const lifecycleOptions = useMemo(() => {
    const present = new Set(
      items
        .map((row) => row.subscriptionStatus)
        .filter((value): value is LifecycleStatus => Boolean(value)),
    );
    return LIFECYCLE_STATUSES.filter((status) => present.has(status));
  }, [items]);

  /*
    A filter that survives its own option is a silently empty table: when the
    selected lifecycle disappears from the data (refetch, tenant changed
    state), fall back to "all" instead of showing zero rows with no visible
    reason.
  */
  useEffect(() => {
    if (lifecycle !== LIFECYCLE_ALL && !lifecycleOptions.includes(lifecycle as LifecycleStatus)) {
      setLifecycle(LIFECYCLE_ALL);
    }
  }, [lifecycle, lifecycleOptions]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return items.filter((row) => {
      if (lifecycle !== LIFECYCLE_ALL && row.subscriptionStatus !== lifecycle) return false;
      if (needle.length === 0) return true;
      return (
        (row.displayName ?? "").toLowerCase().includes(needle) ||
        row.id.toLowerCase().includes(needle)
      );
    });
  }, [items, lifecycle, search]);

  const columns: readonly DataTableColumn<TenantRowDto>[] = [
    {
      id: "name",
      header: t("admin.gtcc.ten.list.column.name"),
      cell: (row) => (
        <button
          type="button"
          data-testid={`gtcc-ten-select-${row.id}`}
          aria-pressed={row.id === selectedId}
          onClick={() => onSelect(row.id)}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "var(--gt-sys-space-1, 4px)",
            background: "transparent",
            border: "none",
            padding: 0,
            cursor: "pointer",
            textAlign: "start",
            font: "inherit",
            color: "inherit",
          }}
        >
          <span style={{ fontWeight: row.id === selectedId ? 700 : 600 }}>
            {row.displayName ?? "—"}
          </span>
          <code
            style={{
              fontSize: "var(--gt-sys-font-size-xs, 12px)",
              color: "var(--gt-sys-text-muted, #6b7280)",
            }}
          >
            {row.id}
          </code>
        </button>
      ),
    },
    {
      id: "type",
      header: t("admin.gtcc.ten.list.column.type"),
      cell: (row) => (row.type ? t(`admin.gtcc.ten.type.${row.type}`) : "—"),
    },
    {
      id: "lifecycle",
      header: t("admin.gtcc.ten.list.column.lifecycle"),
      cell: (row) =>
        row.subscriptionStatus ? (
          <StatePill
            label={t(`admin.gtcc.ten.lifecycle.${row.subscriptionStatus}`)}
            tone={LIFECYCLE_TONES[row.subscriptionStatus]}
            dataTestId={`gtcc-ten-lifecycle-${row.id}`}
          />
        ) : (
          "—"
        ),
    },
    {
      id: "maxDevices",
      header: t("admin.gtcc.ten.list.column.max_devices"),
      align: "end",
      cell: (row) =>
        row.maxDevices === null || row.maxDevices === undefined ? "∞" : row.maxDevices,
    },
    {
      id: "createdAt",
      header: t("admin.gtcc.ten.list.column.created_at"),
      cell: (row) => formatShortDate(row.createdAt),
    },
  ];

  return (
    <Section testId="gtcc-ten-list" heading={t("admin.gtcc.ten.list.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-list-state"
        loadingLabel={t("admin.gtcc.ten.list.loading")}
        forbiddenTitle={t("admin.gtcc.ten.list.forbidden")}
        errorTitle={t("admin.gtcc.ten.list.error")}
        emptyTitle={t("admin.gtcc.ten.list.empty")}
        isEmpty={(data) => (data.items ?? []).length === 0}
        render={() => (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--gt-sys-space-3, 12px)",
            }}
          >
            <TileGrid testId="gtcc-ten-list-tiles">
              <Tile
                label={t("admin.gtcc.ten.list.metric.total")}
                value={formatCount(items.length)}
              />
              {LIFECYCLE_STATUSES.map((status) => (
                <Tile
                  key={status}
                  label={t(`admin.gtcc.ten.list.metric.${status}`)}
                  value={formatCount(
                    items.filter((row) => row.subscriptionStatus === status).length,
                  )}
                />
              ))}
            </TileGrid>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "flex-end",
                gap: "var(--gt-sys-space-3, 12px)",
              }}
            >
              <TextInput
                label={t("admin.gtcc.ten.list.search_label")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                data-testid="gtcc-ten-search"
              />
              <label>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "var(--gt-sys-space-1, 4px)",
                  }}
                >
                  <span
                    style={{
                      color: "var(--gt-sys-text-muted, #6b7280)",
                      fontSize: "var(--gt-sys-font-size-xs, 12px)",
                    }}
                  >
                    {t("admin.gtcc.ten.list.filter.lifecycle")}
                  </span>
                  <select
                    aria-label={t("admin.gtcc.ten.list.filter.lifecycle")}
                    data-testid="gtcc-ten-lifecycle-filter"
                    value={lifecycle}
                    onChange={(event) => setLifecycle(event.currentTarget.value)}
                    style={{
                      background: "var(--gt-sys-surface, #ffffff)",
                      border: "1px solid var(--gt-sys-border, #e5e7eb)",
                      borderRadius: "var(--gt-sys-radius-sm, 6px)",
                      color: "var(--gt-sys-text, #111827)",
                      padding: "var(--gt-sys-space-2, 8px)",
                    }}
                  >
                    <option value={LIFECYCLE_ALL}>{t("admin.gtcc.ten.list.filter.all")}</option>
                    {lifecycleOptions.map((status) => (
                      <option key={status} value={status}>
                        {t(`admin.gtcc.ten.lifecycle.${status}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
              <span
                data-testid="gtcc-ten-list-count"
                style={{
                  color: "var(--gt-sys-text-muted, #6b7280)",
                  fontSize: "var(--gt-sys-font-size-xs, 12px)",
                  fontVariantNumeric: "tabular-nums",
                  paddingBottom: "var(--gt-sys-space-2, 8px)",
                }}
              >
                {t("admin.gtcc.ten.list.filter.count", {
                  shown: filtered.length,
                  total: items.length,
                })}
              </span>
            </div>
            <DataTable
              caption={t("admin.gtcc.ten.list.caption")}
              columns={columns}
              rows={filtered}
              getRowId={(row) => row.id}
              testId="gtcc-ten-table"
              rowTestId={(row) => `gtcc-ten-row-${row.id}`}
              empty={{ title: t("admin.gtcc.ten.list.no_matches") }}
            />
          </div>
        )}
      />
    </Section>
  );
}

/* --------------------------------------------------------------- sections */

/**
 * Der Tenant-Datensatz, den Identitaet, Domain-Readiness und Entitlements
 * teilen. Ein Schluessel, eine Abfrage — React Query fuehrt die Aufrufer
 * zusammen, auch wenn sie in E-GTCC-VIEW-TEN1 in verschiedenen Zeilen des
 * Referenz-Panels haengen.
 */
function useTenantDetailQuery(tenantId: string): UseQueryResult<TenantDetailResponse, Error> {
  return useQuery<TenantDetailResponse, Error>({
    queryKey: ["gtcc", "ten", tenantId, "detail"],
    queryFn: ({ signal }) =>
      apiGet<TenantDetailResponse>(`/v1/admin/tenants/${tenantId}`, undefined, signal),
    staleTime: 10_000,
  });
}

/**
 * Identitaet + Lebenszyklus-Kopf. Exportiert, weil die Referenz-View
 * E-GTCC-VIEW-TEN1 diese Karte als Detail ihrer Zeile "Identität +
 * Lebenszyklus" rahmt (Owner-Auftrag: bestehende Karten wiederverwenden).
 */
export function TenantIdentitySection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const query = useTenantDetailQuery(tenantId);

  return (
    <>
      <Section testId="gtcc-ten-detail" heading={t("admin.gtcc.ten.detail.section")}>
        <QuerySection
          query={query}
          testId="gtcc-ten-detail-state"
          loadingLabel={t("admin.gtcc.ten.detail.loading")}
          forbiddenTitle={t("admin.gtcc.ten.detail.forbidden")}
          errorTitle={t("admin.gtcc.ten.detail.error")}
          emptyTitle={t("admin.gtcc.ten.detail.empty")}
          isEmpty={(data) => !data.tenant}
          render={(data) => {
            const tenant = data.tenant as TenantDetail;
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--gt-sys-space-3, 12px)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--gt-sys-space-3, 12px)",
                    flexWrap: "wrap",
                  }}
                >
                  {/*
                   * `h3`, nicht `h2`: diese Karte haengt in E-GTCC-VIEW-TEN1
                   * unter der Tafel-Ueberschrift (`h3`) einer View, die ihre
                   * eine Ueberschrift der Stufe 2 selbst traegt. Die Typo-Stufe
                   * bleibt unveraendert.
                   */}
                  <h3 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg, 20px)" }}>
                    {tenant.displayName ?? "—"}
                  </h3>
                  {tenant.subscriptionStatus ? (
                    <StatePill
                      label={t(`admin.gtcc.ten.lifecycle.${tenant.subscriptionStatus}`)}
                      tone={LIFECYCLE_TONES[tenant.subscriptionStatus]}
                      dataTestId="gtcc-ten-detail-lifecycle"
                    />
                  ) : null}
                </div>
                <FieldGrid>
                  <Field
                    label={t("admin.gtcc.ten.detail.id_label")}
                    value={<code>{tenant.id}</code>}
                  />
                  <Field
                    label={t("admin.gtcc.ten.detail.slug_label")}
                    value={tenant.slug ? <code>{tenant.slug}</code> : "—"}
                  />
                  <Field
                    label={t("admin.gtcc.ten.detail.trial_until_label")}
                    value={formatShortDate(tenant.trialUntil)}
                  />
                  <Field
                    label={t("admin.gtcc.ten.detail.valid_until_label")}
                    value={formatShortDate(tenant.validUntil)}
                  />
                </FieldGrid>
              </div>
            );
          }}
        />
      </Section>
    </>
  );
}

/**
 * Domain Readiness als eigenstaendige Karte. Die alte Konsole haengt sie an den
 * Kopf; die Referenz von E-GTCC-VIEW-TEN1 listet sie als eigene Zeile, darum
 * der eigene Einstieg. Dieselbe Abfrage, derselbe Schluessel.
 */
export function TenantDomainReadinessSection({ tenantId }: { readonly tenantId: string }) {
  const query = useTenantDetailQuery(tenantId);
  return <DomainReadinessSection query={query} />;
}

/** Kopf der alten Konsole: Identitaet und Domain Readiness untereinander. */
function TenantHeaderSection({ tenantId }: { readonly tenantId: string }) {
  return (
    <>
      <TenantIdentitySection tenantId={tenantId} />
      <TenantDomainReadinessSection tenantId={tenantId} />
    </>
  );
}

/**
 * I-GTCC-19 — the four soft-state lifecycle verbs from gtcc1, backed by the
 * existing platform-owner route. The shared state-machine projection keeps all
 * three admin surfaces in lockstep and excludes terminal states.
 */
export function TenantLifecycleSection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const [selectedAction, setSelectedAction] = useState<TenantLifecycleVerb | null>(null);
  const [lastAction, setLastAction] = useState<TenantLifecycleVerb | null>(null);

  const queryKey = ["gtcc", "ten", tenantId, "detail"] as const;
  const query = useQuery<TenantDetailResponse, Error>({
    queryKey,
    queryFn: ({ signal }) =>
      apiGet<TenantDetailResponse>(`/v1/admin/tenants/${tenantId}`, undefined, signal),
    staleTime: 10_000,
  });

  const mutation = useMutation<TenantLifecycleResponse, Error, TenantLifecycleVerb>({
    mutationFn: (verb) =>
      apiSend<TenantLifecycleResponse>("POST", tenantLifecyclePath(tenantId, verb), {}),
    onMutate: () => setLastAction(null),
    onSuccess: (response, verb) => {
      qc.setQueryData<TenantDetailResponse>(queryKey, { tenant: response.tenant });
      setLastAction(verb);
      setSelectedAction(null);
      void qc.invalidateQueries({ queryKey: ["gtcc", "ten", "tenants"] });
      void qc.invalidateQueries({ queryKey: ["admin", "tenants"] });
    },
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof UnauthenticatedError) {
      return t("admin.tenant_360.error.session_expired_body");
    }
    if (mutation.error instanceof ApiError) {
      return mutation.error.requestId
        ? t("admin.tenant_360.action.error.body_with_request", {
            status: mutation.error.status,
            requestId: mutation.error.requestId,
          })
        : t("admin.tenant_360.action.error.body", { status: mutation.error.status });
    }
    return mutation.error.message;
  })();

  const tenant = query.data?.tenant;

  return (
    <Section testId="gtcc-ten-lifecycle-actions" heading={t("admin.tenant_360.section.actions")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-lifecycle-actions-state"
        loadingLabel={t("admin.gtcc.ten.detail.loading")}
        forbiddenTitle={t("admin.gtcc.ten.detail.forbidden")}
        errorTitle={t("admin.gtcc.ten.detail.error")}
        emptyTitle={t("admin.gtcc.ten.detail.empty")}
        isEmpty={(data) => !data.tenant}
        render={(data) => {
          const current = data.tenant as TenantDetail;
          const currentVerbs = allowedTenantLifecycleVerbs(current.subscriptionStatus);
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--gt-sys-space-2, 8px)",
              }}
            >
              <span style={{ color: "var(--gt-sys-text-muted)" }}>
                {t("admin.tenant_360.section.actions.body")}
              </span>
              {lastAction ? (
                <span role="status" data-testid="gtcc-ten-lifecycle-success">
                  {t("admin.gtcc.ten.lifecycle_action.success", {
                    action: t(`admin.tenant_360.action.${lastAction}`),
                  })}
                </span>
              ) : null}
              {currentVerbs.length === 0 ? (
                <EmptyState title={t("admin.gtcc.ten.lifecycle_action.no_actions")} />
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--gt-sys-space-2, 8px)",
                  }}
                >
                  {currentVerbs.map((verb) => (
                    <Button
                      key={verb}
                      variant="secondary"
                      disabled={mutation.isPending}
                      onClick={() => {
                        mutation.reset();
                        setSelectedAction(verb);
                      }}
                      data-testid={`gtcc-ten-lifecycle-action-${verb}`}
                    >
                      {t(`admin.tenant_360.action.${verb}`)}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          );
        }}
      />

      <ConfirmDialog
        open={selectedAction !== null}
        title={t("admin.tenant_360.section.actions")}
        body={
          selectedAction && tenant
            ? t(`admin.tenant_360.action.confirm.${selectedAction}`, {
                name: tenant.displayName ?? tenant.id,
              })
            : ""
        }
        confirmLabel={
          selectedAction ? t(`admin.tenant_360.action.${selectedAction}`) : t("common.confirm")
        }
        cancelLabel={t("common.cancel")}
        tone={selectedAction === "suspend" || selectedAction === "cancel" ? "danger" : "default"}
        pending={mutation.isPending}
        errorMessage={errorMessage}
        onConfirm={() => {
          if (selectedAction) mutation.mutate(selectedAction);
        }}
        onCancel={() => {
          if (!mutation.isPending) {
            mutation.reset();
            setSelectedAction(null);
          }
        }}
        testIdPrefix="gtcc-ten-lifecycle-confirm"
      />
    </Section>
  );
}

/**
 * No request of its own — gtcc1's readiness card computes the future URL from
 * the stored slug. The read-only port keeps the derivation and drops the input.
 */
function DomainReadinessSection({
  query,
}: {
  readonly query: UseQueryResult<TenantDetailResponse, Error>;
}) {
  const { t } = useLocale();
  return (
    <Section testId="gtcc-ten-domain" heading={t("admin.gtcc.ten.domain.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-domain-state"
        loadingLabel={t("admin.gtcc.ten.domain.loading")}
        forbiddenTitle={t("admin.gtcc.ten.domain.forbidden")}
        errorTitle={t("admin.gtcc.ten.domain.error")}
        emptyTitle={t("admin.gtcc.ten.domain.empty")}
        isEmpty={(data) => !data.tenant?.slug}
        render={(data) => {
          const slug = data.tenant?.slug ?? "";
          const url = buildTenantUrl(slug, DEFAULT_TENANT_BASE_DOMAIN);
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--gt-sys-space-3, 12px)",
              }}
            >
              <FieldGrid>
                <Field label={t("admin.gtcc.ten.domain.slug_label")} value={<code>{slug}</code>} />
                <Field
                  label={t("admin.gtcc.ten.domain.url_label")}
                  value={
                    url ? (
                      <code data-testid="gtcc-ten-domain-url">{url}</code>
                    ) : (
                      t("admin.gtcc.ten.domain.invalid")
                    )
                  }
                />
              </FieldGrid>
              <StatePill
                label={t("admin.gtcc.ten.domain.readiness_only")}
                tone="warn"
                dataTestId="gtcc-ten-domain-readiness"
              />
            </div>
          );
        }}
      />
    </Section>
  );
}

/**
 * C-GTCC-21-WRITE1 — read plus the three writes gtcc1's members card owns.
 * Every mutation targets an existing platform-owner route; nothing new is
 * granted here.
 */
export function MembersSection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const queryKey = ["gtcc", "ten", tenantId, "members"] as const;
  const query = useQuery<MembersResponse, Error>({
    queryKey,
    queryFn: ({ signal }) =>
      apiGet<MembersResponse>(`/v1/admin/tenants/${tenantId}/members`, undefined, signal),
    staleTime: 10_000,
  });

  const [selectedAction, setSelectedAction] = useState<MemberWriteAction | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ["gtcc", "ten", tenantId, "members"] });
    void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "members"] });
  };

  const mutation = useMutation<unknown, Error, MemberWriteAction>({
    mutationFn: (action) =>
      action.kind === "role"
        ? apiSend("PATCH", `/v1/admin/tenants/${tenantId}/members/${action.member.id}/role`, {
            role: action.role,
          })
        : apiSend("PATCH", `/v1/admin/tenants/${tenantId}/members/${action.member.id}/status`, {
            status: action.kind === "suspend" ? "suspended" : "active",
          }),
    onMutate: () => setSuccessMessage(null),
    onSuccess: (_result, action) => {
      setSuccessMessage(
        action.kind === "role"
          ? t("admin.gtcc.ten.members.write.success_role")
          : action.kind === "suspend"
            ? t("admin.gtcc.ten.members.write.success_suspended")
            : t("admin.gtcc.ten.members.write.success_reactivated"),
      );
      setSelectedAction(null);
      invalidate();
    },
  });

  const removeMember = useMutation<void, Error, string>({
    mutationFn: (memberId) =>
      apiSend<void>("DELETE", `/v1/admin/tenants/${tenantId}/members/${memberId}?confirm=hard`),
    onSuccess: () => {
      setSuccessMessage(t("admin.gtcc.ten.members.write.success_removed"));
      invalidate();
    },
  });

  /**
   * Client-side mirror of the server guard (`LastActiveOwnerError`,
   * `services/api/src/modules/admin/platform-members.routes.ts:167`). The 409
   * remains the authority; this only keeps the operator from firing a request
   * that cannot succeed.
   */
  const activeOwnerCount = useMemo(
    () =>
      (query.data?.items ?? []).filter((row) => row.role === "owner" && row.status === "active")
        .length,
    [query.data],
  );
  const isShielded = (row: MemberRow): boolean =>
    row.role === "owner" && row.status === "active" && activeOwnerCount <= 1;

  const writeError = (() => {
    const failure = mutation.error;
    if (!failure) return null;
    if (failure instanceof ApiError && failure.status === 409) {
      return t("admin.gtcc.ten.members.write.error_last_owner");
    }
    return t("admin.gtcc.ten.members.write.error");
  })();

  const confirmBody = (() => {
    if (!selectedAction) return "";
    const member =
      selectedAction.member.email ?? selectedAction.member.displayName ?? selectedAction.member.id;
    if (selectedAction.kind === "role") {
      return t("admin.gtcc.ten.members.write.confirm_role", {
        member,
        role: t(`admin.gtcc.ppl.role.${selectedAction.role}`),
      });
    }
    if (selectedAction.kind === "suspend") {
      return t("admin.gtcc.ten.members.write.confirm_suspend", { member });
    }
    return t("admin.gtcc.ten.members.write.confirm_reactivate", { member });
  })();

  const columns: readonly DataTableColumn<MemberRow>[] = [
    {
      id: "name",
      header: t("admin.gtcc.ten.members.column.name"),
      cell: (row) => row.displayName ?? "—",
    },
    {
      id: "email",
      header: t("admin.gtcc.ten.members.column.email"),
      cell: (row) => row.email ?? "—",
    },
    {
      id: "role",
      header: t("admin.gtcc.ten.members.column.role"),
      cell: (row) => row.role ?? "—",
    },
    {
      id: "status",
      header: t("admin.gtcc.ten.members.column.status"),
      cell: (row) => row.status ?? "—",
    },
    {
      id: "actions",
      header: t("admin.gtcc.ten.members.column.actions"),
      cell: (row) => {
        const shielded = isShielded(row);
        const currentRole = MEMBER_ROLES.find((role) => role === row.role);
        const busy = mutation.isPending || removeMember.isPending;
        return (
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "var(--gt-sys-space-2, 8px)",
            }}
          >
            {currentRole ? (
              <select
                value={currentRole}
                disabled={busy || shielded}
                aria-label={t("admin.gtcc.ten.members.write.role_label")}
                data-testid={`gtcc-ten-member-role-${row.id}`}
                onChange={(event) => {
                  const next = event.currentTarget.value as MemberRole;
                  if (next === currentRole) return;
                  mutation.reset();
                  setSuccessMessage(null);
                  setSelectedAction({ kind: "role", member: row, role: next });
                }}
                style={{
                  padding: "var(--gt-sys-space-1, 4px) var(--gt-sys-space-2, 8px)",
                  borderRadius: "var(--gt-sys-radius-sm)",
                  border: "1px solid var(--gt-sys-border)",
                  background: "var(--gt-sys-surface)",
                  color: "var(--gt-sys-text)",
                  font: "inherit",
                }}
              >
                {MEMBER_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {t(`admin.gtcc.ppl.role.${role}`)}
                  </option>
                ))}
              </select>
            ) : null}
            {row.status === "suspended" ? (
              <Button
                variant="secondary"
                disabled={busy}
                data-testid={`gtcc-ten-member-reactivate-${row.id}`}
                onClick={() => {
                  mutation.reset();
                  setSuccessMessage(null);
                  setSelectedAction({ kind: "reactivate", member: row });
                }}
              >
                {t("admin.gtcc.ten.members.write.reactivate")}
              </Button>
            ) : (
              <Button
                variant="secondary"
                disabled={busy || shielded}
                data-testid={`gtcc-ten-member-suspend-${row.id}`}
                onClick={() => {
                  mutation.reset();
                  setSuccessMessage(null);
                  setSelectedAction({ kind: "suspend", member: row });
                }}
              >
                {t("admin.gtcc.ten.members.write.suspend")}
              </Button>
            )}
            {shielded ? (
              <span
                data-testid={`gtcc-ten-member-last-owner-${row.id}`}
                style={{ color: "var(--gt-sys-text-muted)" }}
              >
                {t("admin.gtcc.ten.members.write.last_owner_hint")}
              </span>
            ) : (
              <DeleteWithConfirm
                triggerLabel={t("admin.gtcc.ten.members.write.remove")}
                entityLabel={t("admin.gtcc.ten.members.write.remove_entity")}
                identifierFieldLabel={t("admin.gtcc.ten.members.write.remove_identifier_field")}
                identifierExpected={row.email ?? row.id}
                warningBody={t("admin.gtcc.ten.members.write.remove_warning")}
                confirmLabel={t("delete.confirm.confirm")}
                cancelLabel={t("common.cancel")}
                typeToConfirmHint={t("delete.confirm.type_to_confirm")}
                errorMessages={{
                  forbidden: t("delete.error.forbidden"),
                  notFound: t("delete.error.not_found"),
                  generic: t("delete.error.generic"),
                }}
                testIdPrefix={`gtcc-ten-member-remove-${row.id}`}
                onConfirm={() => {
                  setSuccessMessage(null);
                  return removeMember.mutateAsync(row.id).then(() => undefined);
                }}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Section testId="gtcc-ten-members" heading={t("admin.gtcc.ten.members.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-members-state"
        loadingLabel={t("admin.gtcc.ten.members.loading")}
        forbiddenTitle={t("admin.gtcc.ten.members.forbidden")}
        errorTitle={t("admin.gtcc.ten.members.error")}
        emptyTitle={t("admin.gtcc.ten.members.empty")}
        isEmpty={(data) => (data.items ?? []).length === 0}
        render={(data) => {
          const memberRows = data.items ?? [];
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--gt-sys-space-2, 8px)",
              }}
            >
              <Tile
                label={t("admin.gtcc.ten.members.count_label")}
                value={formatCount(memberRows.length)}
              />
              {successMessage ? (
                <span role="status" data-testid="gtcc-ten-members-success">
                  {successMessage}
                </span>
              ) : null}
              <DataTable
                caption={t("admin.gtcc.ten.members.caption")}
                columns={columns}
                rows={memberRows}
                getRowId={(row) => row.id}
                testId="gtcc-ten-members-table"
                empty={{ title: t("admin.gtcc.ten.members.empty") }}
              />
            </div>
          );
        }}
      />

      <ConfirmDialog
        open={selectedAction !== null}
        title={t("admin.gtcc.ten.members.write.confirm_title")}
        body={confirmBody}
        confirmLabel={t("common.confirm")}
        cancelLabel={t("common.cancel")}
        tone={selectedAction?.kind === "suspend" ? "danger" : "default"}
        pending={mutation.isPending}
        errorMessage={writeError}
        onConfirm={() => {
          if (selectedAction) mutation.mutate(selectedAction);
        }}
        onCancel={() => {
          if (!mutation.isPending) {
            mutation.reset();
            setSelectedAction(null);
          }
        }}
        testIdPrefix="gtcc-ten-member-confirm"
      />
    </Section>
  );
}

export function InvitationsSection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const query = useQuery<InvitationsResponse, Error>({
    queryKey: ["gtcc", "ten", tenantId, "invitations"],
    queryFn: ({ signal }) =>
      apiGet<InvitationsResponse>(`/v1/admin/tenants/${tenantId}/invitations`, undefined, signal),
    staleTime: 10_000,
  });

  const columns: readonly DataTableColumn<InvitationRow>[] = [
    {
      id: "email",
      header: t("admin.gtcc.ten.invitations.column.email"),
      cell: (row) => row.email ?? "—",
    },
    {
      id: "name",
      header: t("admin.gtcc.ten.invitations.column.name"),
      cell: (row) => row.displayName ?? "—",
    },
    {
      id: "role",
      header: t("admin.gtcc.ten.invitations.column.role"),
      cell: (row) => row.role ?? "—",
    },
    {
      id: "status",
      header: t("admin.gtcc.ten.invitations.column.status"),
      cell: (row) =>
        row.status ? (
          <StatePill tone={INVITATION_TONES[row.status] ?? "neutral"} label={row.status} />
        ) : (
          "—"
        ),
    },
    {
      id: "invited_by",
      header: t("admin.gtcc.ten.invitations.column.invited_by"),
      cell: (row) => row.invitedByEmail ?? "—",
    },
    {
      id: "expires",
      header: t("admin.gtcc.ten.invitations.column.expires"),
      cell: (row) => formatShortDate(row.tokenExpiresAt),
    },
    {
      id: "created",
      header: t("admin.gtcc.ten.invitations.column.created"),
      cell: (row) => formatShortDate(row.createdAt),
    },
  ];

  return (
    <Section testId="gtcc-ten-invitations" heading={t("admin.gtcc.ten.invitations.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-invitations-state"
        loadingLabel={t("admin.gtcc.ten.invitations.loading")}
        forbiddenTitle={t("admin.gtcc.ten.invitations.forbidden")}
        errorTitle={t("admin.gtcc.ten.invitations.error")}
        emptyTitle={t("admin.gtcc.ten.invitations.empty")}
        isEmpty={(data) => (data.items ?? []).length === 0}
        render={(data) => {
          const rows = data.items ?? [];
          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--gt-sys-space-2, 8px)",
              }}
            >
              <Tile
                label={t("admin.gtcc.ten.invitations.count_label")}
                value={formatCount(rows.length)}
              />
              <DataTable
                caption={t("admin.gtcc.ten.invitations.caption")}
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                testId="gtcc-ten-invitations-table"
                empty={{ title: t("admin.gtcc.ten.invitations.empty") }}
              />
            </div>
          );
        }}
      />
    </Section>
  );
}

export function LaunchStatusSection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const query = useQuery<LaunchStatusResponse, Error>({
    queryKey: ["gtcc", "ten", tenantId, "launch-status"],
    queryFn: ({ signal }) =>
      apiGet<LaunchStatusResponse>(
        `/v1/admin/tenants/${tenantId}/launch-status`,
        undefined,
        signal,
      ),
    staleTime: 10_000,
  });

  return (
    <Section testId="gtcc-ten-launch" heading={t("admin.gtcc.ten.launch.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-launch-state"
        loadingLabel={t("admin.gtcc.ten.launch.loading")}
        forbiddenTitle={t("admin.gtcc.ten.launch.forbidden")}
        errorTitle={t("admin.gtcc.ten.launch.error")}
        emptyTitle={t("admin.gtcc.ten.launch.empty")}
        isEmpty={(data) => (data.items ?? []).length === 0}
        render={(data) => (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "var(--gt-sys-space-2, 8px)" }}
          >
            {data.overall ? (
              <StatePill
                label={t(`admin.gtcc.ten.launch.status.${data.overall}`)}
                tone={LAUNCH_TONES[data.overall]}
                dataTestId="gtcc-ten-launch-overall"
              />
            ) : null}
            {(data.items ?? []).map((item) => (
              <div
                key={item.id}
                data-testid={`gtcc-ten-launch-item-${item.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "var(--gt-sys-space-3, 12px)",
                  flexWrap: "wrap",
                }}
              >
                <span style={{ fontSize: "var(--gt-sys-font-size-md, 14px)" }}>
                  {t(`admin.gtcc.ten.launch.item.${item.id}`)}
                </span>
                <StatePill
                  label={t(`admin.gtcc.ten.launch.status.${item.status}`)}
                  tone={LAUNCH_TONES[item.status]}
                />
              </div>
            ))}
          </div>
        )}
      />
    </Section>
  );
}

/**
 * B-GTCC-A10 — entitlements, read-only.
 *
 * No request of its own: the query key is the one `TenantHeaderSection`
 * already uses, so React Query serves both sections from the single
 * `GET /v1/admin/tenants/:id` response instead of issuing a second call.
 *
 * gtcc1 renders `resolvedEntitlements ?? entitlements` in one summary
 * (`apps/admin/src/tenants/TenantDetailPage.tsx:575`), which hides whether a
 * value was written by the operator or derived from the tenant type. This port
 * keeps both columns apart and marks the derived ones, because a `hybrid`
 * tenant with an empty JSONB really does require approval.
 */
export function EntitlementsSection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const query = useQuery<TenantDetailResponse, Error>({
    queryKey: ["gtcc", "ten", tenantId, "detail"],
    queryFn: ({ signal }) =>
      apiGet<TenantDetailResponse>(`/v1/admin/tenants/${tenantId}`, undefined, signal),
    staleTime: 10_000,
  });

  const columns: readonly DataTableColumn<EntitlementRow>[] = [
    {
      id: "entitlement",
      header: t("admin.gtcc.ten.entitlements.column.entitlement"),
      cell: (row) => t(row.labelKey),
    },
    {
      id: "effective",
      header: t("admin.gtcc.ten.entitlements.column.effective"),
      cell: (row) => row.effective,
    },
    {
      id: "policy",
      header: t("admin.gtcc.ten.entitlements.column.policy"),
      cell: (row) => row.policy,
    },
    {
      id: "source",
      header: t("admin.gtcc.ten.entitlements.column.source"),
      cell: (row) => (
        <StatePill
          tone={row.fallback ? "warn" : "success"}
          label={
            row.fallback
              ? t("admin.gtcc.ten.entitlements.source.fallback")
              : t("admin.gtcc.ten.entitlements.source.policy")
          }
        />
      ),
    },
  ];

  return (
    <Section testId="gtcc-ten-entitlements" heading={t("admin.gtcc.ten.entitlements.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-entitlements-state"
        loadingLabel={t("admin.gtcc.ten.entitlements.loading")}
        forbiddenTitle={t("admin.gtcc.ten.entitlements.forbidden")}
        errorTitle={t("admin.gtcc.ten.entitlements.error")}
        emptyTitle={t("admin.gtcc.ten.entitlements.empty")}
        isEmpty={(data) => !data.tenant?.resolvedEntitlements && !data.tenant?.entitlements}
        render={(data) => (
          <DataTable
            caption={t("admin.gtcc.ten.entitlements.caption")}
            columns={columns}
            rows={buildEntitlementRows(data.tenant, t)}
            getRowId={(row) => row.id}
            testId="gtcc-ten-entitlements-table"
            empty={{ title: t("admin.gtcc.ten.entitlements.empty") }}
          />
        )}
      />
    </Section>
  );
}

export function AiCapabilitiesSection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const query = useQuery<AiCapabilitiesResponse, Error>({
    queryKey: ["gtcc", "ten", tenantId, "ai-capabilities"],
    queryFn: ({ signal }) =>
      apiGet<AiCapabilitiesResponse>(
        `/v1/admin/tenants/${tenantId}/ai-capabilities`,
        undefined,
        signal,
      ),
    staleTime: 10_000,
  });

  return (
    <Section testId="gtcc-ten-ai-capabilities" heading={t("admin.gtcc.ten.ai_caps.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-ai-capabilities-state"
        loadingLabel={t("admin.gtcc.ten.ai_caps.loading")}
        forbiddenTitle={t("admin.gtcc.ten.ai_caps.forbidden")}
        errorTitle={t("admin.gtcc.ten.ai_caps.error")}
        emptyTitle={t("admin.gtcc.ten.ai_caps.empty")}
        isEmpty={(data) => !data.aiCapabilities}
        render={(data) => {
          const caps = data.aiCapabilities ?? {};
          const yesNo = (value: boolean | undefined) =>
            value === true
              ? t("admin.gtcc.ten.ai_caps.value.enabled")
              : t("admin.gtcc.ten.ai_caps.value.disabled");
          return (
            <FieldGrid>
              <Field
                label={t("admin.gtcc.ten.ai_caps.gfi_enabled_label")}
                value={yesNo(caps.gfiAiQuestionsEnabled)}
              />
              <Field
                label={t("admin.gtcc.ten.ai_caps.gfi_provider_label")}
                value={providerLabel(caps.gfiProvider, caps.gfiModel)}
              />
              <Field
                label={t("admin.gtcc.ten.ai_caps.reports_enabled_label")}
                value={yesNo(caps.aiReportsEnabled)}
              />
              <Field
                label={t("admin.gtcc.ten.ai_caps.reports_provider_label")}
                value={providerLabel(caps.reportsProvider, caps.reportsModel)}
              />
              <Field
                label={t("admin.gtcc.ten.ai_caps.mode_label")}
                value={caps.mode ? t(`admin.gtcc.ten.ai_caps.mode.${caps.mode}`) : "—"}
              />
            </FieldGrid>
          );
        }}
      />
    </Section>
  );
}

export function AiUsageSection({ tenantId }: { readonly tenantId: string }) {
  const { t } = useLocale();
  const query = useQuery<AiUsageResponse, Error>({
    queryKey: ["gtcc", "ten", tenantId, "ai-usage"],
    queryFn: ({ signal }) =>
      apiGet<AiUsageResponse>(`/v1/admin/tenants/${tenantId}/ai-usage`, undefined, signal),
    staleTime: 10_000,
  });

  return (
    <Section testId="gtcc-ten-ai-usage" heading={t("admin.gtcc.ten.ai_usage.section")}>
      <QuerySection
        query={query}
        testId="gtcc-ten-ai-usage-state"
        loadingLabel={t("admin.gtcc.ten.ai_usage.loading")}
        forbiddenTitle={t("admin.gtcc.ten.ai_usage.forbidden")}
        errorTitle={t("admin.gtcc.ten.ai_usage.error")}
        emptyTitle={t("admin.gtcc.ten.ai_usage.empty")}
        isEmpty={(data) => !data.usage?.totals}
        render={(data) => {
          const totals = data.usage?.totals ?? {};
          /* gtcc1 prints a cost only when the API verified it — never a fake 0.00. */
          const cost =
            data.usage?.costVerified === true
              ? ((totals.estimatedCostMicros ?? 0) / 1_000_000).toFixed(2)
              : t("admin.gtcc.ten.ai_usage.cost_unverified");
          return (
            <TileGrid testId="gtcc-ten-ai-usage-tiles">
              <Tile
                label={t("admin.gtcc.ten.ai_usage.invocations_label")}
                value={formatCount(totals.invocations ?? 0)}
              />
              <Tile
                label={t("admin.gtcc.ten.ai_usage.dry_run_label")}
                value={formatCount(totals.dryRunCount ?? 0)}
              />
              <Tile
                label={t("admin.gtcc.ten.ai_usage.provider_called_label")}
                value={formatCount(totals.providerCalledCount ?? 0)}
              />
              <Tile
                label={t("admin.gtcc.ten.ai_usage.ok_label")}
                value={formatCount(totals.okCount ?? 0)}
              />
              <Tile label={t("admin.gtcc.ten.ai_usage.cost_label")} value={cost} />
            </TileGrid>
          );
        }}
      />
    </Section>
  );
}

/* ------------------------------------------------------------------- view */

export function GtccTenantsView() {
  const { t } = useLocale();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <div
      data-testid="gtcc-view-ten"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--gt-sys-space-4, 16px)",
        padding: "var(--gt-sys-space-5, 24px)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--gt-sys-space-1, 4px)" }}>
        <h2 style={{ margin: 0, fontSize: "var(--gt-sys-font-size-lg, 20px)" }}>
          {t("admin.gtcc.ten.title")}
        </h2>
        <p style={{ margin: 0, color: "var(--gt-sys-text-muted, #6b7280)" }}>
          {t("admin.gtcc.ten.subtitle")}
        </p>
      </div>

      <GtccCreateTenantForm onOpenTenant={setSelectedId} />

      <TenantsListSection selectedId={selectedId} onSelect={setSelectedId} />

      {selectedId === null ? (
        <div data-testid="gtcc-ten-no-selection">
          <EmptyState title={t("admin.gtcc.ten.detail.no_selection")} />
        </div>
      ) : (
        <div
          data-testid="gtcc-ten-360"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--gt-sys-space-4, 16px)",
          }}
        >
          <TenantHeaderSection tenantId={selectedId} />
          <TenantLifecycleSection key={selectedId} tenantId={selectedId} />
          <MembersSection tenantId={selectedId} />
          <TenantDevicesCard tenantId={selectedId} />
          <InvitationsSection tenantId={selectedId} />
          <LaunchStatusSection tenantId={selectedId} />
          <EntitlementsSection tenantId={selectedId} />
          {/* A-ENT-P2 — Karte "Funktionen" (Register Zeile 20, schreibend) */}
          <GtccTenantFeaturesCard tenantId={selectedId} />
          <AiCapabilitiesSection tenantId={selectedId} />
          <AiUsageSection tenantId={selectedId} />
        </div>
      )}

      <p
        data-testid="gtcc-ten-read-only-note"
        style={{
          margin: 0,
          fontSize: "var(--gt-sys-font-size-xs, 12px)",
          color: "var(--gt-sys-text-muted, #6b7280)",
        }}
      >
        {t("admin.gtcc.ten.read_only_note")}
      </p>
    </div>
  );
}
